import { createContext, ReactNode, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Typography, Stack, Box, Link } from '@mui/material'
import { useMe } from '../hooks/useMe'
import InfoDialog from '../components/InfoDialog'
import { Link as RouterLink } from 'react-router'
import { isMobile, isTablet, isClosedInDays } from '../utils/functions'
import tagsImage from '../pages/About/img/client_tutorial/tags.avif'
import useLocalStorage from '../hooks/useLocalStorage'
import { trackEvent } from '../hooks/usePageTracking'
import { useClientDownloadUrl } from '../hooks/useClientDownloadUrl'

const isPCEnvironment = () => !(isMobile() || isTablet())

type PCClientDialogCloses = {
  pc_client_dialog?: number
}

type PCClientDialogContextType = {
  openDialog: () => void
}

export const PCClientDialogContext = createContext<PCClientDialogContextType>(undefined as never)

type Props = {
  children: ReactNode
  is_pc_environment?: () => boolean
}

export const PCClientDialogProvider: React.FC<Props> = ({ children, is_pc_environment = isPCEnvironment }) => {
  const { t } = useTranslation()
  const me = useMe()
  const {download_url} = useClientDownloadUrl()
  const [isOpen, setIsOpen] = useState(false)
  const [dialog_close_times, setDialogCloseTimes] = useLocalStorage<PCClientDialogCloses>('pc_client_dialog_close', {})

  useEffect(() => {
    const shouldShowDialog = () => {
      const now = Date.now()
      if (!is_pc_environment()) return false
      if (!me) return false
      if (me.clear_tutorials.includes('pc_client')) return false
      if (isClosedInDays(dialog_close_times.pc_client_dialog, now, 1)) return false
      return true
    }
    if (shouldShowDialog()) {
      setIsOpen(true)
    }
  }, [me, dialog_close_times.pc_client_dialog, is_pc_environment])

  const handleClose = useCallback(() => {
    setIsOpen(false)
    setDialogCloseTimes(prev => ({
      ...prev,
      pc_client_dialog: Date.now()
    }))
  }, [setDialogCloseTimes])

  const handleDownload = useCallback(async () => {
    if (!download_url) return

    trackEvent('file_download', {
      file_name: 'metamory-sync-tool',
      file_extension: 'exe',
      link_url: download_url,
      link_text: t('myphoto_tutorial.download_now')
    })

    const link = document.createElement('a')
    link.href = download_url
    link.download = ''
    link.click()

    handleClose()
  }, [download_url, t, handleClose])

  const openDialog = useCallback(() => {
    setIsOpen(true)
  }, [])

  const dialogMessage = (
    <Stack spacing={2}>
      <Typography variant="h6" fontWeight='bold'>
        {t('myphoto_tutorial.title')}
      </Typography>
      <Typography variant="body2" fontSize='inherit'>
        {t('tutorial.pc_client_description')}
      </Typography>

      <Box sx={{
        display: 'flex',
        gap: 3,
        alignItems: 'center',
        flexWrap: 'wrap'
      }}>
        <Box sx={{
          flex: '0 0 auto',
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: '0 0 8px 0 rgba(0, 0, 0, 0.3)'
        }}>
          <img
            src={tagsImage}
            alt="PC Client features"
            style={{
              width: '256px',
              display: 'block'
            }}
          />
        </Box>

        <Box sx={{ flex: 1, minWidth: '200px' }}>
          <Typography variant="body2" fontSize={14}>
            {t('myphoto_tutorial.description')}
            <ul style={{ marginTop: 8, paddingLeft: 20 }}>
              <li>{t('myphoto_tutorial.li1')}</li>
              <li>{t('myphoto_tutorial.li2')}</li>
              <li>{t('myphoto_tutorial.li3')}</li>
            </ul>
          </Typography>
          <br/>
          <Typography variant="body2" fontSize={14}>
            {t('myphoto_tutorial.tutorial_link_text')}
            <Link component={RouterLink} to="/about/client_tutorial" target="_blank" sx={{ ml: 0.5 }}>
              {t('myphoto_tutorial.tutorial_link')}
            </Link>
            {t('myphoto_tutorial.tutorial_link_suffix')}
          </Typography>
        </Box>
      </Box>

    </Stack>
  )

  return (
    <PCClientDialogContext value={{ openDialog }}>
      {children}
      <InfoDialog
        open={isOpen}
        onClose={handleClose}
        message={dialogMessage}
        ok_text={t('myphoto_tutorial.download_now')}
        action={handleDownload}
        variant='contained'
      />
    </PCClientDialogContext>
  )
}
